import React from 'react';
import cx from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { Link } from 'react-router-dom';
import IconButton from '@material-ui/core/IconButton';
import { ReactComponent as ForwardArrow } from 'assets/icons/arrow.svg';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import { ReactComponent as ProgressIcon } from 'assets/icons/progress.svg';
import { toUserTimezone } from 'utils';
import Chip from '@material-ui/core/Chip';
import { ROLES } from '../../../../constant/userTypes';
import PRODUCTS from 'constant/product-types';

const PRODUCTS_LOOKUP = PRODUCTS.reduce((memo, i) => {
  memo[i.value] = i.label;
  return memo;
}, {});

const useStyles = makeStyles((theme) => ({
  root: {
    'display': 'flex',
    'alignItems': 'center',
    'backgroundColor': theme.palette.background.default,
    'marginBottom': theme.spacing(2),
    'borderRadius': 6,
    'padding': theme.spacing(2),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  timeLeftChip: {
    backgroundColor: theme.palette.primary['100'],
    color: theme.palette.primary.main,
    borderRadius: 6,
    fontSize: 14,
    fontWeight: 700,
    marginRight: theme.spacing(2),
  },
  soonChip: {
    'color': theme.palette.error.main,
    'backgroundColor': theme.palette.error.light,
    '& .MuiChip-icon': {
      fontSize: 16,
      color: theme.palette.primary.main,
    },
  },
  iconButton: {
    'marginRight': -8,
    '& .MuiSvgIcon-root': {
      fontSize: '24px !important',
    },
  },
  tag: {
    marginLeft: theme.spacing(1),
    fontSize: 12,
    fontWeight: 400,
  },
}));

const getIcon = (status) => {
  if (status === 'done')
    return <CheckBoxIcon fontSize="small" color="primary" />;
  if (status === 'inprogress') return <ProgressIcon color="#2A7EBB" />;
  return <CheckBoxOutlineBlankIcon fontSize="small" color="primary" />;
};

const ExerciseListItem = ({
  exercise,
  module,
  studentExercise,
  user,
  currentIndex,
  from,
  student,
}) => {
  const classes = useStyles();
  const { id, due_date, status, user_id } = studentExercise || {};
  const dueDays = due_date
    ? toUserTimezone(due_date, user.timezone).diff(
        toUserTimezone(new Date(), user.timezone),
        'd',
      )
    : -1;

  const disableActions = user.type === ROLES.PARENT;
  const routePath =
    user.type === ROLES.STUDENT
      ? `/exercises/worksheet/${id}`
      : `/students/${user_id}/exercises/${id}/view`;

  return (
    <Box className={classes.root}>
      <Box marginRight={2}>{getIcon(status)}</Box>
      <Box flex={1}>
        <Box display="flex" alignItems="center">
          <Typography variant="body2">{exercise.name}</Typography>
          {exercise.tags && (
            <Chip
              variant="outlined"
              size="small"
              label={exercise.tags}
              className={classes.tag}
              color={exercise.tags === 'Recommended' ? 'primary' : 'default'}
            />
          )}
        </Box>
        <Typography variant="caption">
          {module.name} • {exercise.duration} min
        </Typography>
      </Box>
      <Box display="flex" alignItems="center">
        {due_date && status !== 'done' && (
          <Chip
            className={cx(
              classes.timeLeftChip,
              dueDays < 7 && classes.soonChip,
            )}
            label={
              <>
                {dueDays === 1 && '1 day left'}
                {dueDays > 1 && `${dueDays} days left`}
                {dueDays < 0 && 'past due'}
                {dueDays === 0 && 'due today'}
              </>
            }
          />
        )}
        {!disableActions && (
          <Link
            to={{
              pathname: routePath,
              state: { from: from, currentIndex },
            }}
            onClickCapture={() =>
              window.analytics.track('Exercise started', {
                id,
                due_date,
                status,
                user_id,
                module,
                exercise,
                product:
                  student?.product_type &&
                  PRODUCTS_LOOKUP[student.product_type],
              })
            }
          >
            <IconButton
              color="primary"
              edge="end"
              className={classes.iconButton}
            >
              <ForwardArrow />
            </IconButton>
          </Link>
        )}
      </Box>
    </Box>
  );
};

export default ExerciseListItem;
